<template>
  <div>
    <label
      class="form-label text-xs text-uppercase font-weight-bold text-muted"
      >{{ label }}</label
    >
    <div class="position-relative">
      <div
        class="form-control d-flex flex-wrap gap-2 align-items-center"
        style="min-height: 42px; cursor: text"
        @click="$refs.searchInput.focus()"
      >
        <span
          v-for="tag in selectedTags"
          :key="tag.id"
          class="badge bg-secondary text-white rounded-pill pe-2"
        >
          {{ tag.name }}
          <i
            class="fas fa-times ms-1 cursor-pointer"
            @click.stop="removeTag(tag)"
          ></i>
        </span>
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="border-0 p-0 shadow-none flex-grow-1"
          style="outline: none; min-width: 100px"
          placeholder="Search or create tags..."
          @keydown.enter.prevent="createTag"
          @keydown.backspace="onBackspace"
          @focus="showDropdown = true"
        />
      </div>

      <!-- Dropdown -->
      <div
        v-if="showDropdown && (filteredTags.length > 0 || search)"
        class="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
        style="max-height: 200px; overflow-y: auto; z-index: 9999"
      >
        <div
          v-for="tag in filteredTags"
          :key="tag.id"
          class="px-3 py-2 cursor-pointer hover-bg-light"
          @click="selectTag(tag)"
        >
          {{ tag.name }}
          <span class="text-xs text-muted ms-2">({{ tag.slug }})</span>
        </div>
        <div
          v-if="search && !filteredTags.find((t) => t.name === search)"
          class="px-3 py-2 cursor-pointer text-primary hover-bg-light border-top"
          @click="createTag"
        >
          <i class="fas fa-plus me-1"></i> Create "{{ search }}"
        </div>
      </div>
    </div>
    <!-- Overlay to close dropdown -->
    <div
      v-if="showDropdown"
      class="position-fixed top-0 start-0 w-100 h-100 z-0"
      @click="showDropdown = false"
    ></div>
  </div>
</template>

<script>
import request from "@/utils/request";

export default {
  name: "TagSelector",
  props: {
    modelValue: {
      type: Array, // Array of Tag IDs
      default: () => [],
    },
    label: {
      type: String,
      default: "Tags",
    },
  },
  data() {
    return {
      allTags: [],
      search: "",
      showDropdown: false,
    };
  },
  computed: {
    selectedTags() {
      return this.allTags.filter((t) => this.modelValue.includes(t.id));
    },
    filteredTags() {
      const searchLower = this.search.toLowerCase();
      return this.allTags
        .filter(
          (t) =>
            !this.modelValue.includes(t.id) && // Not selected
            (t.name.toLowerCase().includes(searchLower) ||
              t.slug.toLowerCase().includes(searchLower))
        )
        .slice(0, 10);
    },
  },
  created() {
    this.fetchTags();
  },
  methods: {
    async fetchTags() {
      try {
        const res = await request.get("/api/admin/tags");
        this.allTags = res.data.data;
      } catch (e) {
        console.error("Failed to fetch tags", e);
      }
    },
    selectTag(tag) {
      if (!this.modelValue.includes(tag.id)) {
        this.$emit("update:modelValue", [...this.modelValue, tag.id]);
      }
      this.search = "";
      this.showDropdown = false;
    },
    removeTag(tag) {
      const newValue = this.modelValue.filter((id) => id !== tag.id);
      this.$emit("update:modelValue", newValue);
    },
    onBackspace() {
      if (
        this.search === "" &&
        this.selectedTags.length > 0 &&
        !this.backspacePrimed
      ) {
        this.backspacePrimed = true; // Wait for second backspace to delete? Or just delete last.
        // Let's just delete last for simplicty
        const lastTag = this.selectedTags[this.selectedTags.length - 1];
        this.removeTag(lastTag);
      }
    },
    async createTag() {
      if (!this.search.trim()) return;

      const name = this.search.trim();
      // Check if exists in allTags (even if filtered out or selected)
      const existing = this.allTags.find(
        (t) => t.name.toLowerCase() === name.toLowerCase()
      );

      if (existing) {
        this.selectTag(existing);
        return;
      }

      // Create new
      try {
        const slug = name.toLowerCase().replace(/\s+/g, "-");
        const res = await request.post("/api/admin/tags", {
          name,
          slug,
          type: "other", // Default type
          is_active: true,
        });
        const newTag = res.data.data || res.data; // Adjust based on API response structure
        this.allTags.push(newTag);
        this.selectTag(newTag);
      } catch (e) {
        console.error("Failed to create tag", e);
        // Maybe show toast
      }
    },
  },
};
</script>

<style scoped>
.hover-bg-light:hover {
  background-color: #f8f9fa;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
